import { CASO_DA, REPORT_DA } from "@/lib/caso";
import type { EntityKind, Priority, TipoDerivato } from "@/lib/types";

export const CATEGORIES: string[] = [
  "Intelligence",
  "Economia",
  "Marittimo",
  "Flotta Ombra",
  "Riservato",
  "Politica",
  "Militare",
  "False Flag",
];

export const ENTITY_LABELS: Record<EntityKind, string> = {
  persons: "Persone",
  companies: "Aziende",
  institutions: "Istituzioni",
  organizations: "Organizzazioni",
  parties: "Partiti",
  media: "Media",
  places: "Luoghi",
  sectors: "Settori",
  figures: "Valori / Indici",
  tech: "Tecnologie",
};

export const ENTITY_SYMBOLS: Record<EntityKind, string> = {
  persons: "◆",
  companies: "▣",
  institutions: "▲",
  organizations: "◎",
  parties: "▽",
  media: "▷",
  places: "◉",
  sectors: "▤",
  figures: "§",
  tech: "⬡",
};

/**
 * Tinte sobrie per ogni tipo di entità — sperimentale, per dare un po'
 * di colore al sito monocromatico senza saturazioni cartoon. Usate
 * principalmente nella sidebar "Entità menzionate" come simbolo
 * colorato del gruppo.
 */
export const ENTITY_COLORS: Record<EntityKind, string> = {
  persons: "var(--e-persons)",           // violetto
  companies: "var(--e-companies)",       // indaco
  institutions: "var(--e-institutions)", // crimson
  organizations: "var(--e-organizations)", // ciano
  parties: "var(--e-parties)",           // oliva
  media: "var(--e-media)",               // magenta
  places: "var(--e-places)",             // verde
  sectors: "var(--e-sectors)",           // ambra
  figures: "var(--e-figures)",           // grigio
  tech: "var(--e-tech)",                 // teal
};

/**
 * Toni dei banner servizi: tre tinte fisse per posizione (viola, teal,
 * terracotta). Li usano i banner della sidebar dell'articolo
 * (ServiceBanner), il box dei numeri di ArticleView e i tre passi del
 * modulo di /contatti, così la palette dei servizi è una sola. Erano
 * nati per i tile della spalla servizi della home, che non esiste più.
 */
export const SERVICE_POSITION_TONES = [
  "#6c4cb6", // viola
  "#2c7c7c", // teal
  "#8e4d3a", // terracotta
] as const;

export const ENTITY_KINDS: EntityKind[] = [
  "persons",
  "companies",
  "institutions",
  "organizations",
  "parties",
  "media",
  "places",
  "sectors",
  "figures",
  "tech",
];

export const PRIORITY_MAP: Record<Priority, { label: string; ord: number }> = {
  // Il pezzo firmato della settimana, che apre la prima pagina. Primo
  // nell'ordine perché è il più editoriale di tutti.
  editoriale: { label: "EDITORIALE", ord: -1 },
  "teatri-crisi": { label: "TEATRI DI CRISI", ord: 0 },
  "nuovi-scenari": { label: "NUOVI SCENARI", ord: 1 },
  "segnali-deboli": { label: "SEGNALI DEBOLI", ord: 2 },
  ibrido: { label: "IBRIDO", ord: 3 },
  focus: { label: "FOCUS", ord: 4 },
  notizie: { label: "NOTIZIE", ord: 5 },
  report: { label: "REPORT", ord: 6 },
  pmi: { label: "PMI", ord: 7 },
  articoli: { label: "ARTICOLI", ord: 8 },
  archivio: { label: "ARCHIVIO", ord: 9 },
};

/** Come si dice "tutti quelli di questo tipo", tipo per tipo.
 *
 *  Serve al rimando "vedi tutto" del riquadro "Il resto del rullo"
 *  (<HomeAltriDispacci>), che segue il chip acceso. Prima la frase si
 *  componeva da sé, `Tutti i ${label.toLowerCase()}`, e reggeva solo
 *  sui tipi maschili plurali che cominciano per consonante: cinque chip
 *  su dieci uscivano sgrammaticati, "Tutti i editoriale", "Tutti i
 *  notizie", "Tutti i ibrido", "Tutti i articoli", "Tutti i pmi", che
 *  per giunta spegneva anche la sigla.
 *
 *  La frase sta scritta per intero, articolo compreso, perché in
 *  italiano non si ricava dall'etichetta: cambia il genere (le notizie,
 *  gli editoriali), cambia l'articolo davanti a vocale e a s+consonante
 *  (gli ibridi, i segnali), e due tipi non hanno un plurale proprio
 *  (`ibrido` e `pmi` sono qualifiche, non nomi, quindi prendono
 *  "dispacci" come sostantivo). Una tabella di soli plurali avrebbe
 *  richiesto comunque una seconda tabella di articoli.
 *
 *  È un `Record<Priority, string>` completo e non un parziale: così un
 *  tipo nuovo in PRIORITY_MAP non compila finché non ha anche la sua
 *  frase, ed è il modo per non ritrovarsi le due tabelle divergenti come
 *  successe a PRIORITY_STEM. */
export const PRIORITY_TUTTI: Record<Priority, string> = {
  editoriale: "Tutti gli editoriali",
  "teatri-crisi": "Tutti i teatri di crisi",
  "nuovi-scenari": "Tutti i nuovi scenari",
  "segnali-deboli": "Tutti i segnali deboli",
  ibrido: "Tutti i dispacci ibridi",
  focus: "Tutti i focus",
  notizie: "Tutte le notizie",
  report: "Tutti i report",
  pmi: "Tutti i dispacci PMI",
  articoli: "Tutti gli articoli",
  archivio: "Tutto l'archivio",
};

/** Ordine unico dei tipi, da usare ovunque servisse una lista di
 *  Priority. Prima la produzione editoriale, in fondo l'archivio
 *  storico, che da solo è il 99% dei dispacci: in cima schiaccerebbe
 *  tutto il resto, e la vetrina "In evidenza" pesca proprio in
 *  quest'ordine. */
export const PRIORITY_KEYS: Priority[] = (
  Object.keys(PRIORITY_MAP) as Priority[]
).sort((a, b) => PRIORITY_MAP[a].ord - PRIORITY_MAP[b].ord);

/** Il rullo: tutti i tipi tranne l'archivio storico.
 *
 *  È la definizione di "rullo" del sito, e vale la pena scriverla una
 *  volta sola perché la leggono in molti: la pagina /rullo (vedi
 *  RULLO_HREF qui sotto), la prima pagina, la ricerca e il banco di
 *  prova del carattere (app/prova-carattere).
 *
 *  Perché l'archivio resta fuori: da solo è il 98,7% dei dispacci
 *  (101.818 su 103.166 il 14/09/2026) ed è fermo da anni, nemmeno una
 *  voce con data nell'ultimo mese. Una lista che lo comprende non è il
 *  rullo delle notizie, è il magazzino: i 1.348 dispacci veri restano
 *  sepolti sotto centomila pezzi del 2011. */
export const RULLO_PRIORITIES: Priority[] = PRIORITY_KEYS.filter(
  (p) => p !== "archivio"
);

/** Dove porta la voce RULLO della barra: la pagina «Rullo informativo»
 *  (app/rullo), che senza parametri mostra già tutti i tipi del rullo e
 *  nessun dispaccio d'archivio storico. Fino al 29/09/2026 era
 *  `/ricerca?priority=` con i dieci tipi per esteso; /rullo era un
 *  rimando a /ricerca ed è diventata la pagina vera.
 *
 *  `/ricerca` resta quello che era, cioè tutto l'archivio compreso lo
 *  storico: è l'indirizzo su cui atterrano i link per anno di /archivio
 *  (`?from=`/`?before=`, che /rullo non legge). */
export const RULLO_HREF = "/rullo";

/** Il Rullo ristretto ai soli casi (`?tipo=caso`, la voce «Casi» del menu
 *  «Tipo di dispaccio»): ci portano il titolo «Casi. Le storie che
 *  tornano.» della prima pagina e il suo «Tutti i casi ↗». Scritto qui e
 *  non a mano nei componenti, come `RULLO_HREF`. Il parametro lo legge e
 *  lo scrive `leggiStato` / `hrefRullo` in lib/rullo.ts. */
export const CASI_HREF = `${RULLO_HREF}?tipo=caso`;

/** Il Rullo ristretto ai segnali deboli (`?priority=segnali-deboli`, il tipo
 *  a monte): ci porta «Tutti i segnali deboli ↗» del «punto» in prima
 *  pagina. Dal 04/11/2026 (richiesta: «deve portare al rullo, segnare tutti
 *  i segnali deboli»); prima andava alla vecchia pagina /segnali-deboli. */
export const SEGNALI_HREF = `${RULLO_HREF}?priority=segnali-deboli`;

/** La scritta che accompagna il marchio, in due righe: identità e campo.
 *  Una sola, letta dalla testata (components/header/Header.tsx) e dal
 *  piede (components/footer/FooterHome.tsx). Fino al 02/10/2026 il piede
 *  aveva la sua copia, rimasta a «Geopolitica, OSINT e analisi di
 *  scenario / Italia · Mediterraneo · Oltre» dopo che la testata era
 *  passata a questa. */
export const DESCRIZIONE_SITO = {
  identita: "La cibernetica dell’informazione",
  campo: "Territorio · Imprese · Mercati",
} as const;

/** Dove vive la raccolta «Editoriali» (app/editoriali, fino al
 *  30/09/2026 in anteprima su /anteprima/editoriali, che ora rimanda
 *  qui con un 308). Sta qui e non in
 *  lib/editoriali-raccolta.ts perché la leggono anche testata e piede,
 *  che sono client e sono su ogni rotta: importarla da
 *  là porterebbe nel loro bundle i testi dei provvisori. Come il Rullo,
 *  la pagina prende testata e piede della prima pagina. */
export const EDITORIALI_PATH = "/editoriali";

/** Quanti dispacci per pagina nel rullo: /ricerca e la sua anteprima
 *  (app/anteprima/rullo) sfogliano con lo stesso passo, così «pagina 14»
 *  vuol dire la stessa cosa nelle due viste. Sta qui e non in
 *  lib/ricerca.ts perché lo legge anche lib/rullo.ts, che finisce nel
 *  grafo client (la testata ne importa il percorso). */
export const RICERCA_PAGE_SIZE = 100;

/* I temi sotto osservazione della prima pagina stavano qui (`TEMI_HOME`):
   dal 15/10/2026 sono `TEMI_PRIMA_PAGINA` in lib/prima-pagina.ts, con la
   loro icona, e quattro su cinque atterrano sul rullo filtrato per
   categoria invece che sulla ricerca per testo. */

/** Quanti dispacci recenti la prima pagina si tiene da parte per farli
 *  girare negli scomparti sotto l'editoriale.
 *
 *  Ventiquattro, cioè una giornata piena e qualcosa: l'11/09/2026 il
 *  sito ha pubblicato 21 dispacci non d'archivio (Focus 8, Nuovi scenari
 *  5, Segnali deboli 3, Report 2, Notizie 2, Ibrido 1), il 10/09 ne
 *  aveva pubblicati 13. Con ventiquattro il giro copre tutto quello che
 *  è uscito oggi anche in una giornata piena, e nelle ore di magra
 *  pesca le ultime di ieri invece di restare corto.
 *
 *  Non è un filtro sulla data, ed è voluto: alle sei del mattino un
 *  "solo oggi" avrebbe un dispaccio solo e la lastra resterebbe mezza
 *  vuota. Sono semplicemente gli ultimi ventiquattro. */
/** Quanti report mostra il blocco «Report. Oltre la notizia.» in cima alla
 *  prima pagina (<ReportInApertura>, dal 25/10/2026): cinque, una voce per
 *  filone-report, come nel riferimento (uploads/blocco-report-home.html).
 *  Fino ad allora erano i tre della fascia più in basso, che non c'è più.
 *
 *  Sta qui e non accanto al componente perché app/page.tsx lo legge: una
 *  costante esportata da un modulo "use client" e letta dal server non è
 *  un numero (vedi LEAD_DISPACCI). */
export const HOME_REPORTS = 5;

export const POOL_ROTAZIONE = 24;

/** Ogni quanti secondi cambia il giro degli scomparti.
 *
 *  La prima pagina è una rotta dinamica (`ƒ` nel build): la funzione
 *  gira a ogni richiesta, quindi il giro si può calcolare dall'orologio
 *  del server e cambia da sé a ogni ricaricamento, senza JavaScript nel
 *  browser, senza differenze fra server e idratazione e senza salti di
 *  impaginazione. Le fetch restano in cache un minuto: a cambiare è
 *  quali dei dispacci già scaricati si mostrano, non quante volte si
 *  chiede a monte.
 *
 *  Cinque secondi e non zero: due ricaricamenti a raffica mostrano lo
 *  stesso giro, e va bene così. Chi torna sulla pagina dopo mezzo
 *  minuto trova altre notizie, chi preme F5 due volte di seguito non
 *  vede le carte mescolarsi sotto le dita. */
export const ROTAZIONE_SECONDI = 5;

/** Quante notizie secondarie stanno sotto la notizia dominante della
 *  prima pagina (<HomeLead>).
 *
 *  Quattro dal 28/09/2026, ed erano tre: l'apertura è diventata una
 *  notizia dominante più quattro secondarie, cioè cinque notizie in
 *  tutto. La griglia è `lg:grid-cols-4`, due colonne da `sm` e una sul
 *  telefono, e quattro la chiudono esatta a tutte e tre le misure.
 *  Arrivano dalla coda del giro di app/page.tsx, cioè da quello che il
 *  riquadro "Il resto del rullo" e la fascia dei dossier non stanno già
 *  mostrando: cambiando questo numero si allarga la finestra del giro
 *  (`LEAD_DISPACCI + 1`) e non serve toccare nient'altro.
 *
 *  Sta qui e non accanto al componente perché <HomeLead> è un Client
 *  Component: una costante esportata da un modulo "use client" e letta
 *  da un Server Component non è un numero, è un riferimento al modulo
 *  client, e finiva così dentro il `pageSize` della query. */
/* Tre e non più quattro dal 15/10/2026: il disegno nuovo mette sotto il
   blocco d'apertura una fila di tre notizie, `lg:grid-cols-3`. */
export const LEAD_DISPACCI = 3;

/** La firma quando il dispaccio non ne porta una sua.
 *
 *  Il campo `author` a monte è vuoto su tutto l'archivio storico e lo
 *  resterà anche dopo, perché non tutti i pezzi sono firmati da una
 *  persona: quelli non firmati sono della casa, che è esattamente
 *  quello che la pagina articolo già scriveva a mano ("Di Osservatorio
 *  Italiano"). Sta qui e non in due componenti perché la firma di
 *  ripiego compare in prima pagina e sull'articolo, e due copie a mano
 *  divergono, come è già successo con le tabelle delle priorità. */
export const FIRMA_REDAZIONE = "Osservatorio Italiano";

/** La firma da stampare per un dispaccio: la sua, se ce l'ha, la casa
 *  altrimenti. `author` arriva già ripulito da nd-transform (null e non
 *  stringa vuota), ma il `.trim()` regge anche chi costruisce un
 *  NewsItem a mano. */
export function firmaDi(author?: string | null): string {
  return author?.trim() || FIRMA_REDAZIONE;
}

/** La scala dei periodi del menu "Periodo", scritta una volta sola.
 *
 *  Era in cinque posti e non erano d'accordo: `app/ricerca/page.tsx` e
 *  `app/page.tsx` conoscevano tre chiavi (`1h`, `12h`, `24h`), il proxy
 *  `app/api/cases/route.ts` ne conosceva dieci (comprese `1d`, `180d`,
 *  `730d`) e il menu ne offriva tre. Effetto misurato: `?date=7d`
 *  arrivava alla pagina, che non lo riconosceva e non mandava nessun
 *  `$gte`, mentre il "carica altri" passava dal proxy, che invece lo
 *  capiva. Prima pagina non filtrata e resto della lista filtrato, senza
 *  che niente lo dicesse. Una tabella sola rende impossibile che tornino
 *  a divergere, come già per PRIORITY_STEM.
 *
 *  Perché la scala arriva all'anno. Il ritmo di pubblicazione dei tipi
 *  editoriali è di circa dieci dispacci al giorno, 69 a settimana e 299
 *  al mese (contati a monte il 10/09/2026): sotto le 24 ore la lista si
 *  svuota spesso, e con le sole tre fasce brevi non c'era modo di
 *  chiedere "l'ultimo trimestre". L'archivio storico, che è il 98,7% dei
 *  dispacci, è fermo da anni: un periodo lungo non lo tira dentro, lo
 *  esclude comunque. */
export type PeriodoKey =
  | "1h"
  | "12h"
  | "24h"
  | "7d"
  | "30d"
  | "90d"
  | "365d";

/** Il valore del filtro periodo, "tutti" compreso. */
export type DateMode = "all" | PeriodoKey;

/** Le voci del menu, in ordine. L'etichetta è quella che si legge nel
 *  menu del sito, nel pannello del telefono e nel riquadro "nessun
 *  dispaccio": scritta una volta, non tre. */
export const PERIODI: { value: PeriodoKey; label: string; minuti: number }[] = [
  { value: "1h", label: "Ultima ora", minuti: 60 },
  { value: "12h", label: "Ultime 12 ore", minuti: 720 },
  { value: "24h", label: "Ultime 24 ore", minuti: 1440 },
  { value: "7d", label: "Ultimi 7 giorni", minuti: 10_080 },
  { value: "30d", label: "Ultimi 30 giorni", minuti: 43_200 },
  { value: "90d", label: "Ultimi 90 giorni", minuti: 129_600 },
  { value: "365d", label: "Ultimo anno", minuti: 525_600 },
];

export const PERIODO_KEYS: PeriodoKey[] = PERIODI.map((p) => p.value);

export const PERIODO_LABEL: Record<PeriodoKey, string> = Object.fromEntries(
  PERIODI.map((p) => [p.value, p.label])
) as Record<PeriodoKey, string>;

/** Chiavi che il menu non offre più ma che possono ancora arrivare da un
 *  indirizzo vecchio: il proxy le accettava e il `select` del rullo le
 *  proponeva. Restano leggibili, così un link salvato non smette di
 *  filtrare; semplicemente non c'è più niente che le produca. */
const PERIODI_STORICI: Record<string, number> = {
  "1d": 1440,
  "180d": 259_200,
  "730d": 1_051_200,
};

/** Quanti minuti indietro guarda un periodo, o `undefined` se la chiave
 *  non è una di quelle note. Unico punto di lettura: chi filtra passa di
 *  qui e non ha una tabella sua. */
export function minutiDelPeriodo(v?: string | null): number | undefined {
  if (!v) return undefined;
  const voce = PERIODI.find((p) => p.value === v);
  if (voce) return voce.minuti;
  return PERIODI_STORICI[v];
}

/** `true` se la chiave è una voce viva del menu. Serve dove il valore
 *  deve tornare nello stato dei filtri, che le chiavi storiche non
 *  possono rappresentare. */
export function isPeriodo(v?: string | null): v is PeriodoKey {
  return !!v && (PERIODO_KEYS as string[]).includes(v);
}

/** Frammento distintivo del campo `type` a monte, cercato con
 *  `$containsi`. È la sola tabella che traduce fra i nostri tipi e
 *  quelli dell'API, e la usano sia il filtro (nd-client) sia
 *  l'etichetta mostrata sul dispaccio (nd-transform).
 *
 *  Prima erano due tabelle separate e non erano d'accordo: i 101.818
 *  dispacci di type "Archivio" venivano etichettati NOTIZIE ma il chip
 *  NOTIZIE non li trovava, perché cercava "notiz". Tenerne una sola
 *  rende impossibile che tornino a divergere.
 *
 *  Valori a monte, verificati sull'intero archivio:
 *    Archivio 101.818 · Focus 501 · Nuovi scenari 181 · Notizie 146 ·
 *    Teatri di crisi 52 · Ibrido 41 · Report 23 · Segnali deboli 21 ·
 *    Piccole e Medie Imprese 3 · Articoli 1  =  102.787, cioè tutto. */
export const PRIORITY_STEM: Record<Priority, string> = {
  // A monte questo tipo va ancora creato: finché non esiste la query
  // torna zero risultati e il blocco in prima pagina non si mostra.
  editoriale: "editorial",
  "teatri-crisi": "teatri",
  "nuovi-scenari": "scenari",
  "segnali-deboli": "segnali",
  ibrido: "ibrid",
  focus: "focus",
  notizie: "notiz",
  report: "report",
  // A monte il valore è "Piccole e Medie Imprese": lo stem "pmi" non lo
  // conteneva e il chip restituiva zero.
  pmi: "piccole",
  articoli: "articol",
  archivio: "archiv",
};

/** La tavolozza delle card di prodotto editoriale (<ReportCard>): sei
 *  tinte con cui si ruota una griglia, perché una griglia di una tinta
 *  sola non distingue una scheda dall'altra e una di dodici diventa un
 *  arcobaleno.
 *
 *  Erano sei esadecimali scritti a mano in tre posti — /report,
 *  /segnali-deboli e la fascia dossier della prima pagina — e in tema
 *  scuro restavano i valori del chiaro: il bordo di una card indaco
 *  (#2a4ba0) sulla carta scura stava a 1,7:1, cioè spariva, e le tre
 *  righe di testo in tono se la cavavano solo perché `.oi-tono-testo`
 *  le rimescolava. Qui sono i toni editoriali `--p-*`, dichiarati due
 *  volte in globals.css con i contrasti già misurati, quindi la card
 *  segue i due temi da sé.
 *
 *  La traduzione è la stessa già usata dal vecchio <CiberneticaExplorer> (tolto il 17/10/2026), che
 *  ha fatto lo stesso passaggio sui suoi sei capitoli: le tinte
 *  restano riconoscibili, cambia solo quanto sono chiare o scure.
 *
 *    indaco     --p-report          (era #2a4ba0, identico nel chiaro)
 *    teal       --p-ibrido          (era #2c7c7c)
 *    ambra      --p-segnali-deboli  (era #a8742a)
 *    viola      --p-nuovi-scenari   (era #6c4cb6)
 *    terracotta --p-pmi             (era #8e4d3a)
 *    verde      --p-articoli        (era #3e7c4b)
 *
 *  Sulla terracotta: l'esadecimale di prima era esattamente `--p-place`,
 *  che però non si ridichiara in `.dark` (è il pallino del paese, stessa
 *  tinta nei due temi, per scelta) e sulla carta scura sta a 2,9:1.
 *  `--p-pmi` è la stessa famiglia e la coppia ce l'ha.
 *
 *  Sono nomi di variabile, non colori: un suffisso di opacità
 *  appiccicato all'esadecimale (`${tone}0d`) qui non vale più, perché
 *  `var(--p-pmi)0d` non è un colore. Le trasparenze si fanno con
 *  `color-mix` — vedi `velo()` in <ReportCard>. */
export const TONI_CARD = {
  indaco: "var(--p-report)",
  teal: "var(--p-ibrido)",
  ambra: "var(--p-segnali-deboli)",
  viola: "var(--p-nuovi-scenari)",
  terracotta: "var(--p-pmi)",
  verde: "var(--p-articoli)",
} as const;

/** L'ordine con cui ruotano i toni nell'archivio dei dossier e nella
 *  fascia dossier della prima pagina. Le due viste devono leggersi come
 *  la stessa cosa, quindi la sequenza è una sola.
 *
 *  In prima pagina i dossier sono tre e i toni sei: la rotazione non si
 *  chiude mai e le tre card della riga hanno tre tinte diverse. */
export const REPORT_TONES: readonly string[] = [
  TONI_CARD.teal,
  TONI_CARD.viola,
  TONI_CARD.indaco,
  TONI_CARD.verde,
  TONI_CARD.ambra,
  TONI_CARD.terracotta,
];

/** Lo stesso giro per l'archivio dei segnali deboli, che parte da un
 *  altro punto: le due pagine hanno lo stesso impianto, e una sequenza
 *  sfalsata è quello che dice a colpo d'occhio in quale delle due si è
 *  finiti. */
export const SEGNALI_TONES: readonly string[] = [
  TONI_CARD.terracotta,
  TONI_CARD.indaco,
  TONI_CARD.ambra,
  TONI_CARD.teal,
  TONI_CARD.viola,
  TONI_CARD.verde,
];

/** Le soglie del tipo derivato, cioè di che cosa è un dispaccio misurato
 *  sul numero di voci della sua cronologia (`/case-histories` con lo
 *  stesso `agenzia_id`). È l'unica fonte di verità: la legge il server
 *  quando compone i `NewsItem` (`conTipoDerivato` in lib/nd-client.ts) e
 *  la leggono i componenti per l'etichetta. Sta qui e non accanto a un
 *  componente perché un valore esportato da un file "use client" e letto
 *  dal server non è il valore ma il riferimento al modulo client.
 *
 *  Zero o una voce è una **notizia**: la cronologia ha al massimo la
 *  versione che si sta leggendo (l'ultima voce coincide col dispaccio
 *  vivo); dal 21/10/2026 anche due voci sono ancora una notizia. Da tre a
 *  otto è un **caso**, una vicenda tornata qualche volta. Da nove in su
 *  è un **report**, un filone seguito nel tempo (la regola della
 *  redazione dice da 9 a 15; il più lungo ne ha 29 e resta report). Le
 *  soglie sono quelle della pagina del dispaccio (`CASO_DA` e `REPORT_DA`
 *  in lib/caso.ts): dal 15 al 21/10/2026 erano caso da due e report da
 *  quattro, prima ancora report da cinque.
 *
 *  Distribuzione misurata il 02/10/2026 su 3.398 voci di cronologia e
 *  1.578 `agenzia_id` distinti: 1.256 con una voce, 146 da due a quattro,
 *  176 da cinque in su. L'archivio storico ha `agenzia_id` numerici che
 *  nessuna cronologia porta, quindi esce tutto "notizia".
 *
 *  Dal 25/10/2026 i casi **si filtrano**: `/rullo?tipo=caso` (`CASI_HREF`,
 *  la voce «Casi» del menu «Tipo di dispaccio») mostra i dispacci dei
 *  filoni da `caso.daVoci` a sotto `report.daVoci` voci (`soloCasi` in
 *  lib/report-dati.ts, 214 filoni il 25/10/2026), come /report mostra
 *  quelli da `report.daVoci` in su (`soloReport`), e `/rullo?tipo=report`
 *  mostra gli stessi report nel Rullo (le due voci del gruppo «Filoni»). È un parametro a sé e
 *  non un `?priority=`, perché a monte il caso non è un `type`. Gli id sono
 *  troppi per un `$in` solo (nginx risponde 414 oltre ~8 kB di URL), e la
 *  lista li chiede a blocchi (`listCasesDelleAgenzie` in lib/nd-client.ts).
 *
 *  L'etichetta sulle righe resta invece **solo testo**: non porta da
 *  nessuna parte (righe del rullo, flusso e apertura della prima pagina).
 *  Resta una soglia sulle voci, non un raggruppamento redazionale: quello
 *  arriverà quando a monte ci sarà un campo del caso (`case_id` esiste ma
 *  oggi è vuoto su tutti i dispacci, vedi i learnings). */
export const TIPO_DERIVATO: Record<TipoDerivato, { label: string; daVoci: number }> = {
  notizia: { label: "Notizia", daVoci: 0 },
  caso: { label: "Caso", daVoci: CASO_DA },
  report: { label: "Report", daVoci: REPORT_DA },
};

export function tipoDaVoci(n: number): TipoDerivato {
  if (n >= TIPO_DERIVATO.report.daVoci) return "report";
  if (n >= TIPO_DERIVATO.caso.daVoci) return "caso";
  return "notizia";
}

/** Quanto aspettare dall'ultimo cambio di un menu di filtro prima di
 *  navigare: 400 ms, meno di una richiesta a monte (0,6-0,9 s). Due
 *  `router.push` in volo insieme fanno vincere il primo e il secondo
 *  cambio sparisce (vedi learnings, «Due `router.push` in volo
 *  insieme»): col ritardo una raffica di cambi diventa una navigazione
 *  sola. Lo leggono <FeedClient>, <RicercaRegistro>, <BarraRicerca> e
 *  la barra del Rullo (<BarraRullo>): un valore solo per tutte le
 *  barre del sito. */
export const RITARDO_FILTRI = 400;
